/**
 * SessionFeedbackCard.jsx
 *
 * 3-tap micro-rating shown at the end of every Private Suite session.
 * Quietly trains the per-user `max_affirmations` calibration so the
 * experience self-tunes — fewer lines for nervous systems that found
 * it too much, more for those who felt it thin.
 *
 * Three states: idle (showing the three pills) → submitting →
 * thank-you (auto-vanishes after 2.2s).
 */
import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOK = {
  cream:    "#F3EDDD",
  creamDim: "rgba(243, 237, 221, 0.6)",
  goldSoft: "#D7BB7A",
  goldMist: "rgba(215, 187, 122, 0.16)",
};

const CHOICES = [
  { key: "thin",       label: "Too thin",   tail: "more, please" },
  { key: "just_right", label: "Just right", tail: "felt aligned" },
  { key: "too_much",   label: "Too much",   tail: "ease it back" },
];

export default function SessionFeedbackCard({ sessionId, mode, activity, focusArea }) {
  const [state, setState] = useState("idle"); // idle | submitting | done
  const [picked, setPicked] = useState(null);

  async function pick(choice) {
    if (state !== "idle") return;
    setPicked(choice);
    setState("submitting");
    try {
      const token = localStorage.getItem("reclaim_token");
      await axios.post(
        `${API}/api/private-suite/sessions/${sessionId}/rating`,
        { rating: choice, mode, activity, focus_area: focusArea },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      /* silent — feedback is gravy, not blocking */
    }
    setState("done");
  }

  // Hidden when no session_id (preview / sample flows)
  if (!sessionId) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.65, duration: 0.7 }}
      className="mb-10 text-center"
      data-testid="session-feedback-card"
    >
      <p
        className="uppercase tracking-[0.32em] mb-4"
        style={{
          color: TOK.goldSoft,
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.55rem",
        }}
      >
        {state === "done" ? "Thank you — calibrating" : "How did this session feel?"}
      </p>
      <AnimatePresence mode="wait">
        {state === "done" ? (
          <motion.div
            key="done"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{
              background: "rgba(215, 187, 122, 0.18)",
              border: `1px solid ${TOK.goldSoft}`,
              color: TOK.cream,
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              fontSize: "0.95rem",
            }}
            data-testid="session-feedback-thanks"
          >
            <Check size={12} style={{ color: TOK.goldSoft }} />
            Your next session will adjust to "{CHOICES.find((c) => c.key === picked)?.label}"
          </motion.div>
        ) : (
          <motion.div
            key="pills"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-wrap justify-center gap-2"
          >
            {CHOICES.map((c) => {
              const dim = state === "submitting" && picked !== c.key;
              const active = picked === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  disabled={state !== "idle"}
                  onClick={() => pick(c.key)}
                  className="px-4 py-2 rounded-full transition-all"
                  style={{
                    background: active ? "rgba(215, 187, 122, 0.22)" : "transparent",
                    border: `1px solid ${active ? TOK.goldSoft : TOK.goldMist}`,
                    color: dim ? TOK.creamDim : TOK.cream,
                    opacity: dim ? 0.5 : 1,
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.65rem",
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                  }}
                  data-testid={`session-feedback-${c.key}`}
                >
                  {c.label}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
